import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from '../controls/advanced-gridview/services/advanced-service';
import { ViewCloningData } from '../controls/view-cloning-dialog/models/view-cloning-data';
import { IViewAccessibility } from '../models/interfaces/view-accessibility.interface';
import { IView } from '../models/interfaces/view.interface';
import { View } from '../models/view';
import { ViewAccessibility } from '../models/view-accessibility';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class ViewsService extends CrudService implements AdvancedService<View> {
    /**
     * Liste des vues de l'organisation.
     */
    private organisationViews = new BehaviorSubject<View[]>(null);

    /**
     * retourne la liste des vues de l'organisation.
     */
    public get organisationViews$() {
        return this.organisationViews.asObservable();
    }

    /**
     * retourne la liste des vues de l'organisation.
     */
    public get currentOrganisationViews() {
        return this.organisationViews.value;
    }

    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<View>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Views`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<View>(View, x);
                return res;
            })
        );
    }

    listAll(): Observable<View[]> {
        // Appel de la Web Api
        return this.http.get<IView[]>(this.baseUrl + `/api/organizations/${this.organizationId}/Views`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                var res = View.fromArray(x);

                res = res.sort((a, b) => a.name.localeCompare(b.name));

                this.organisationViews.next(res);
                return res;
            })
        );
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos): Observable<Blob> {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Views/export`, { headers: headers, responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }

    update(viewId: number, view: View): Observable<View> {
        // Appel de la Web Api
        return this.http.put<View>(this.baseUrl + `/api/Organizations/${this.organizationId}/Views/${viewId}`, view, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                if (this.organisationViews.value) {
                    let index = this.organisationViews.value.findIndex((a) => a.id === x.id);
                    this.organisationViews.value[index] = x;
                    this.organisationViews.next(this.organisationViews.value);
                }

                return x;
            })
        );
    }

    delete(viewId: number): Observable<boolean> {
        // Appel de la Web Api
        return this.http.delete<View>(this.baseUrl + `/api/Organizations/${this.organizationId}/Views/${viewId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                let views = this.organisationViews.value;
                let index = views.findIndex((x) => x.id === viewId);
                views.splice(index, 1);
                this.organisationViews.next(
                    views.sort((a, b) => {
                        return a.name < b.name ? -1 : 1;
                    })
                );
                return true;
            })
        );
    }

    add(view: View): Observable<View> {
        // Appel de la Web Api
        return this.http.post<View>(this.baseUrl + `/api/Organizations/${this.organizationId}/Views`, view, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                let views = this.organisationViews.value;
                views.push(x);
                this.organisationViews.next(
                    views.sort((a, b) => {
                        return a.name < b.name ? -1 : 1;
                    })
                );
                return x;
            })
        );
    }

    activateArchiving(viewId: number): Observable<View> {
        // Appel de la Web Api
        return this.http.patch<View>(this.baseUrl + `/api/organizations/${this.organizationId}/views/${viewId}/archiving`, null, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                let views = this.organisationViews.value;
                views.push(x);
                this.organisationViews.next(
                    views.sort((a, b) => {
                        return a.name < b.name ? -1 : 1;
                    })
                );
                return x;
            })
        );
    }

    clone(view: View, data: ViewCloningData): Observable<View> {
        // Appel de la Web Api
        return this.http.post<View>(this.baseUrl + `/api/Organizations/${this.organizationId}/Views/${view.id}`, data, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                let views = this.organisationViews.value;
                views.push(x);
                this.organisationViews.next(
                    views.sort((a, b) => {
                        return a.name < b.name ? -1 : 1;
                    })
                );
                return x;
            })
        );
    }

    updateAccessibilities(viewId: number, accessibilities: IViewAccessibility[]): Observable<IViewAccessibility[]> {
        // Appel de la Web Api
        return this.http
            .post<IViewAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Views/${viewId}/Accessibilities`, accessibilities, this.getDefaultHttpOptions())
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    get(viewId: number): Observable<View> {
        // Appel de la Web Api
        return this.http.get<View>(this.baseUrl + `/api/Organizations/${this.organizationId}/Views/${viewId}`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    getAccessibilities(viewId: number): Observable<ViewAccessibility[]> {
        // Appel de la Web Api
        return this.http.get<ViewAccessibility[]>(this.baseUrl + `/api/Organizations/${this.organizationId}/Views/${viewId}/accessibilities`, this.getDefaultHttpOptions()).pipe(
            map((x) => {
                return x;
            })
        );
    }

    exportAccessibilities(viewId: number): Observable<Blob> {
        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/Views/${viewId}/accessibilities/export`, { responseType: 'blob' }).pipe(
            map((res: any) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                return blob;
            })
        );
    }
}
